
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, FormField, Input, TextArea, Dropdown, Message, Segment, Header, Grid, GridColumn, Button, Confirm, Modal, Icon } from "semantic-ui-react";
import { IcctDatePicker } from '../../common/IcctDatePicker';
import { fetchWithAuth, getCSRFToken, useAuth } from "../../auth";
import { formatDateToMmDdYyyy } from "../../../utils/formatDate";
import { formatPhoneNumber, safeFormatPhoneNumber, getRawPhoneNumber, validatePhoneNumber } from '../../../utils/phoneNumber';

const camelToSnake = (str) => {
	return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

function maskLatLngInput(value, type) {
	// Check if user manually entered a decimal point
	const hasManualDecimal = value.includes('.');

	if (hasManualDecimal) {
		let v = value.replace(/[^0-9.-]/g, '');
		// Only one minus at the start
		v = v.replace(/(?!^)-/g, '');
		// Only one decimal point
		const parts = v.split('.');
		if (parts.length > 2) {
			v = parts[0] + '.' + parts.slice(1).join('');
		}
		// Limit decimal places to 8
		if (parts.length === 2 && parts[1].length > 8) {
			v = parts[0] + '.' + parts[1].slice(0, 8);
		}
		return v;
	} else {
		let v = value.replace(/[^0-9-]/g, '');
		// Only one minus at the start
		v = v.replace(/(?!^)-/g, '');
		let isNegative = v.startsWith('-');
		let digits = v.replace(/-/g, '');
		if (!digits) return isNegative ? '-' : '';
		// For latitude: format as -XX.XXXXXXX, for longitude: -XXX.XXXXXXX
		let intLen = type === 'latitude' ? 2 : 3;
		if (digits.length <= intLen) {
			return (isNegative ? '-' : '') + digits;
		}
		let intPart = digits.slice(0, intLen);
		let decPart = digits.slice(intLen, intLen + 8); // up to 8 decimals
		return (isNegative ? '-' : '') + intPart + (decPart ? '.' + decPart : '');
	}
}

export const UtilityStrikeFormDetails = () => {
	const [form, setForm] = useState({
		reporter_name: '',
		reporter_phone: '',
		reporter_email: '',
		reporter_company: '',
		address_location: '',
		nearest_intersection: '',
		region: '',
		state_damaged_located: '',
		latitude: '',
		longitude: '',
		icc_or_contractor: '',
		was_site_marked: false,
		site_marked_with: '',
		marks_accurate: false,
		damage_date: formatDateToMmDdYyyy(new Date()),
		damage_time: '12:00',
		utility_type: '',
		tds_project_number: '',
		utility_damaged: '',
		utility_damaged_other_description: '',
		locate_state_811_contacted: false,
		dig_811_ticket_number: '',
		excavation_type_used: '',
		damage_depth_inches: '',
		describe_what_happened: '',
		evacuation_required: false,
		evacuation_how_many: '',
		evacuation_notes: '',
		additional_notes: '',
		attachments: [],
		team: null,
	});
	const [teams, setTeams] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [inputErrors, setInputErrors] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [showImageModal, setShowImageModal] = useState(false);
	const [modalImageSrc, setModalImageSrc] = useState(null);
	const { id } = useParams();
	const navigate = useNavigate();
	const { isReadOnly } = useAuth();
	const isNew = id === undefined || id === 'new';

	const fetchForm = async () => {
		if (isNew) {
			setLoaded(true);
			return;
		}
		try {
			const response = await fetchWithAuth(
				process.env.REACT_APP_ICC_TOOL_API_HOST + `utility-strike/${id}/`,
				{
					headers: { 'X-CSRFToken': getCSRFToken() },
					credentials: 'include'
				}
			);
			if (!response.ok) throw new Error('Failed to fetch Utility Strike form');
			const data = await response.json();
			// Split backend damageDateTime (ISO 8601) into local date and time
			let damage_date = '';
			let damage_time = '';
			if (data.damageDateTime) {
				const dt = new Date(data.damageDateTime);
				// Use UTC parts to avoid timezone offset issues
				const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
				const dd = String(dt.getUTCDate()).padStart(2, '0');
				const yyyy = dt.getUTCFullYear();
				damage_date = `${mm}/${dd}/${yyyy}`;
				// Format as HH:mm (24h) for the time input (UTC)
				damage_time = dt.toISOString().substring(11, 16);
			}

			const mappedForm = {};

			Object.keys(data).forEach(key => {
				if (!['damageDateTime', 'attachments', 'reporterPhone'].includes(key)) {
					const snakeKey = camelToSnake(key);

					if (typeof data[key] === 'boolean') {
						mappedForm[snakeKey] = data[key];
					} else if (typeof data[key] === 'number') {
						mappedForm[snakeKey] = String(data[key]);
					} else {
						mappedForm[snakeKey] = data[key] || '';
					}
				}
			});

			setForm({
				...mappedForm,
				reporter_phone: safeFormatPhoneNumber(data.reporterPhone || ''),
				latitude: data.latitude !== undefined ? String(data.latitude) : '',
				longitude: data.longitude !== undefined ? String(data.longitude) : '',
				damage_depth_inches: data.damageDepthInches !== undefined ? String(data.damageDepthInches) : '',
				evacuation_how_many: data.evacuationHowMany !== undefined ? String(data.evacuationHowMany) : '',
				damage_date,
				damage_time,
				attachments: Array.isArray(data.attachments)
					? data.attachments.map(a => (a.imageBase64 ? a.imageBase64 : (typeof a === 'string' ? a : null))).filter(Boolean)
					: [],
				team: data.team || null,
			});
			setIsSubmitted(data.submissionTime !== null);
		} catch (err) {
			setError('Failed to load Utility Strike form details');
			console.error('Error fetching Utility Strike form:', err);
		}
	};

	const fetchTeams = async () => {
		try {
			const response = await fetchWithAuth(
				process.env.REACT_APP_ICC_TOOL_API_HOST + 'user/teams/',
				{
					headers: { 'X-CSRFToken': getCSRFToken() },
					credentials: 'include'
				}
			);
			if (response.ok) {
				const data = await response.json();
				const teamOptions = data.map(team => ({ key: team.id, value: team.id, text: team.name }));
				setTeams(teamOptions);
			}
		} catch (err) {
			console.error('Error fetching teams:', err);
		}
	};

	useEffect(() => {
		const loadData = async () => {
			await Promise.all([
				fetchForm(),
				fetchTeams(),
			]);
			setLoaded(true);
		};
		loadData();
	}, [id]);


	function maskInchesInput(value) {
		// Remove all non-digit characters
		let digits = value.replace(/\D/g, '');
		if (!digits) return '';
		// If less than 3 digits, treat as integer (no dot)
		if (digits.length <= 2) return digits;
		// Insert dot before last two digits
		let intPart = digits.slice(0, -2);
		let decPart = digits.slice(-2);
		// Remove leading zeros from intPart, but allow '0' if empty
		intPart = intPart.replace(/^0+(?!$)/, '');
		return intPart + '.' + decPart;
	}

	const handleChange = (e, { name, value, type, checked }) => {
		if (name === 'reporter_phone') {
			const formattedValue = formatPhoneNumber(value);
			setForm(prev => ({ ...prev, [name]: formattedValue }));
		} else if (name === 'latitude' || name === 'longitude') {
			const masked = maskLatLngInput(value, name);
			setForm(prev => ({ ...prev, [name]: masked }));
		} else if (name === 'damage_depth_inches') {
			const masked = maskInchesInput(value);
			setForm(prev => ({ ...prev, [name]: masked }));
		} else {
			setForm(prev => ({
				...prev,
				[name]: type === 'checkbox' ? checked : value
			}));
		}
		if (inputErrors[name]) {
			setInputErrors(prev => ({ ...prev, [name]: null }));
		}
	};

	const handleTimeChange = (e, { value }) => {
		if (/^([01]?\d|2[0-3]):[0-5]\d$/.test(value) || value === '') {
			setForm(prev => ({ ...prev, damage_time: value }));
			if (inputErrors.damage_date_time) {
				setInputErrors(prev => ({ ...prev, damage_date_time: null }));
			}
		}
	};

	// Email validation helper
	const validateEmail = (email) => {
		if (!email) return true; // Email is optional
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Number validation helper
	const validateNumber = (value, fieldName) => {
		if (!value) return null; // If empty, no error (unless required)
		const num = parseInt(value);
		if (isNaN(num) || num < 0) {
			return `${fieldName} must be a positive number`;
		}
		if (num > 10000) {
			return `${fieldName} seems unreasonably high. Please verify.`;
		}
		return null;
	};

	const validateCoordinate = (value, type) => {
		if (!value) return `${type} is required. Please provide GPS coordinates for precise location.`;
		const num = parseFloat(value);
		if (isNaN(num)) return `${type} must be a valid number.`;
		const min = type === 'Latitude' ? -90 : -180;
		const max = type === 'Latitude' ? 90 : 180;
		if (num < min || num > max) return `${type} must be between ${min} and ${max} degrees.`;
		return null;
	};

	const validateDepth = (value) => {
		if (!value) return null;
		if (isNaN(Number(value))) return 'Depth must be a number';
		if (parseFloat(value) < 0) return 'Depth must be positive';
		if (/\.\d{3,}/.test(value)) return 'Max 2 decimal places';
		return null;
	};

	const validateForm = () => {
		const errors = {};

		// Required field validations with comprehensive messages
		if (!form.reporter_name.trim()) {
			errors.reporter_name = 'Reporter name is required. Please provide the full name of the person reporting this incident.';
		}

		if (!form.reporter_phone.trim()) {
			errors.reporter_phone = 'Reporter phone number is required for follow-up contact.';
		} else if (!validatePhoneNumber(form.reporter_phone)) {
			errors.reporter_phone = 'Please enter a valid 10-digit US phone number in format (XXX) XXX-XXXX.';
		}

		// Optional email validation
		if (form.reporter_email.trim() && !validateEmail(form.reporter_email)) {
			errors.reporter_email = 'Please enter a valid email address (e.g., user@example.com).';
		}

		if (!form.address_location.trim()) {
			errors.address_location = 'Address or location of damage is required. Please provide specific street address or landmark.';
		}

		if (!form.nearest_intersection.trim()) {
			errors.nearest_intersection = 'Nearest intersection is required to help locate the incident site.';
		}

		if (!form.region.trim()) {
			errors.region = 'Region is required. Please specify the geographic region where the incident occurred.';
		}

		if (!form.state_damaged_located.trim()) {
			errors.state_damaged_located = 'State is required. Please specify the state where the damage occurred.';
		}

		const latError = validateCoordinate(form.latitude, 'Latitude');
		if (latError) errors.latitude = latError;

		const lngError = validateCoordinate(form.longitude, 'Longitude');
		if (lngError) errors.longitude = lngError;

		if (!form.damage_date || !form.damage_time) {
			errors.damage_date_time = !form.damage_date
				? 'Date of damage is required. When did this incident occur?'
				: 'Time of damage is required. Please specify the approximate time of incident.';
		}

		if (!form.utility_type.trim()) {
			errors.utility_type = 'Utility type is required. Please specify what type of utility was damaged.';
		}

		if (!form.tds_project_number.trim()) {
			errors.tds_project_number = 'TDS Project Number is required. Please provide the project number associated with this work.';
		}

		if (!form.utility_damaged.trim()) {
			errors.utility_damaged = 'Utility damaged description is required. Please specify exactly what was damaged.';
		}

		if (!form.dig_811_ticket_number.trim()) {
			errors.dig_811_ticket_number = '811 Ticket Number is required. Please provide the ticket number from your call to 811.';
		}

		if (!form.damage_depth_inches) {
			errors.damage_depth_inches = 'Damage depth is required. Please specify how deep the damage occurred in inches.';
		} else {
			const depthError = validateNumber(form.damage_depth_inches, 'Damage depth');
			if (depthError) errors.damage_depth_inches = depthError;
		}

		// Conditional validations
		if (form.evacuation_required && form.evacuation_how_many.trim()) {
			const evacuationError = validateNumber(form.evacuation_how_many, 'Number of people evacuated');
			if (evacuationError) errors.evacuation_how_many = evacuationError;
		}

		if (!form.team) {
			errors.team = 'Team assignment is required. Please select the team responsible for this incident.';
		}

		setInputErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const saveForm = async (submit = false) => {
		if (!validateForm()) {
			setError('Please fill in all required fields');
			return;
		}
		setSaving(true);
		setError('');
		try {
			let headers = { 'X-CSRFToken': getCSRFToken() };
			const url = isNew
				? process.env.REACT_APP_ICC_TOOL_API_HOST + 'utility-strike/'
				: process.env.REACT_APP_ICC_TOOL_API_HOST + `utility-strike/${id}/`;
			const method = isNew ? 'POST' : 'PUT';

			// Merge damage_date and damage_time into ISO string for damage_date_time
			let damageDateTime = null;
			if (form.damage_date && form.damage_time) {
				const [month, day, year] = form.damage_date.split('/');
				if (month && day && year) {
					const mm = month.padStart(2, '0');
					const dd = day.padStart(2, '0');
					const isoString = `${year}-${mm}-${dd}T${form.damage_time}`;
					damageDateTime = isoString;
				}
			}

			headers['Content-Type'] = 'application/json';
			let payload = {
				...form,
				damage_date_time: damageDateTime,
				reporter_phone: getRawPhoneNumber(form.reporter_phone),
				submission_time: submit ? new Date().toISOString() : null,
			};

			if (Array.isArray(form.attachments)) {
				payload.attachments = form.attachments.filter(a => typeof a === 'string' && a.startsWith('data:image'));
			} else {
				payload.attachments = [];
			}
			const response = await fetchWithAuth(url, {
				method,
				headers,
				credentials: 'include',
				body: JSON.stringify(payload)
			});
			if (!response.ok) {
				try {
					const data = await response.json();

					// Handle field-specific errors from backend
					if (data && typeof data === 'object') {
						const backendErrors = {};
						let hasFieldErrors = false;

						// Map common backend field error patterns
						Object.keys(data).forEach(key => {
							if (Array.isArray(data[key]) && data[key].length > 0) {
								// Backend typically returns arrays of error messages
								const errorMessage = data[key][0];

								const frontendField = camelToSnake(key);
								backendErrors[frontendField] = errorMessage;
								hasFieldErrors = true;
							}
						});

						if (hasFieldErrors) {
							setInputErrors(backendErrors);
							setError('Please correct the highlighted fields above.');
						} else {
							// General error message
							const errorMsg = data.error || data.message || data.detail || 'Failed to save Utility Strike form. Please check all fields and try again.';
							setError(errorMsg);
						}
					} else {
						setError('Failed to save Utility Strike form. Please check all fields and try again.');
					}
				} catch (e) {
					// If we can't parse the error response, show a general error
					setError('Failed to save Utility Strike form. Please check your internet connection and try again.');
				}
				setSaving(false);
				return;
			}
		} catch (err) {
			setError(err.message || 'Failed to save Utility Strike form');
			console.error('Error saving Utility Strike form:', err);
		}
		setSaving(false);
	};

	const saveDraft = () => saveForm(false);
	const handleSubmitClick = () => setShowConfirm(true);
	const handleConfirmSubmit = () => { setShowConfirm(false); saveForm(true); };
	const handleCancelSubmit = () => setShowConfirm(false);

	if (!loaded) {
		return <div>Loading...</div>;
	}

	return (
		<div style={{ maxWidth: '1000px', margin: '0 auto' }}>
			{error && <Message negative>{error}</Message>}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
				<a
					href="https://tdsinc.sharepoint.com/sites/TDS-InternalConstruction/SitePages/Utility-Strike-Process.aspx"
					target="_blank"
					rel="noopener noreferrer"
					style={{ marginRight: 16 }}
				>
					<Icon name="external alternate" /> Utility Strike Process
				</a>
			</div>
			<Header as="h2">
				{isNew ? 'Create New Utility Strike Form' : `Utility Strike Form - ${form.reporter_name || 'Untitled'}`}
				{isSubmitted && <span style={{ color: 'green', marginLeft: '10px' }}>(Submitted)</span>}

			</Header>
			<Form>
				<Segment>
					<Header as="h4">Reporter Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.reporter_name}>
								<label>Reporter Name</label>
								<Input name="reporter_name" value={form.reporter_name} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.reporter_name && <div style={{ color: 'red' }}>{inputErrors.reporter_name}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.reporter_phone}>
								<label>Reporter Phone</label>
								<Input
									name="reporter_phone"
									value={form.reporter_phone}
									onChange={handleChange}
									disabled={isSubmitted}
									placeholder="(555) 123-4567"
								/>
								{inputErrors.reporter_phone && <div style={{ color: 'red' }}>{inputErrors.reporter_phone}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField error={!!inputErrors.reporter_email}>
								<label>Reporter Email</label>
								<Input
									name="reporter_email"
									value={form.reporter_email}
									onChange={handleChange}
									disabled={isSubmitted}
									placeholder="user@example.com"
								/>
								{inputErrors.reporter_email && <div style={{ color: 'red' }}>{inputErrors.reporter_email}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField>
								<label>Reporter Company</label>
								<Input name="reporter_company" value={form.reporter_company} onChange={handleChange} disabled={isSubmitted} />
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Location Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.address_location}>
								<label>Address/Location of Damage</label>
								<Input name="address_location" value={form.address_location} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.address_location && <div style={{ color: 'red' }}>{inputErrors.address_location}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.nearest_intersection}>
								<label>Nearest Intersection</label>
								<Input name="nearest_intersection" value={form.nearest_intersection} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.nearest_intersection && <div style={{ color: 'red' }}>{inputErrors.nearest_intersection}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.region}>
								<label>Region</label>
								<Input name="region" value={form.region} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.region && <div style={{ color: 'red' }}>{inputErrors.region}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.state_damaged_located}>
								<label>State Damaged Located</label>
								<Input name="state_damaged_located" value={form.state_damaged_located} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.state_damaged_located && <div style={{ color: 'red' }}>{inputErrors.state_damaged_located}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.latitude}>
								<label>Latitude</label>
								<Input
									name="latitude"
									value={form.latitude}
									onChange={handleChange}
									onBlur={e => {
										const val = e.target.value;
										let error = '';
										if (val === '' || isNaN(Number(val))) {
											error = 'Latitude must be a number';
										} else {
											const num = parseFloat(val);
											if (num < -90 || num > 90) error = 'Latitude must be between -90 and 90';
											// Only allow up to 8 decimals
											if (/\.\d{9,}/.test(val)) error = 'Latitude max 8 decimals';
										}
										if (error) setInputErrors(prev => ({ ...prev, latitude: error }));
									}}
									inputMode="decimal"
									pattern="^-?([1-8]?\d(\.\d{1,8})?|90(\.0{1,8})?)$"
									disabled={isSubmitted}
									placeholder="e.g. 40.7128"
								/>
								{inputErrors.latitude && <div style={{ color: 'red' }}>{inputErrors.latitude}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.longitude}>
								<label>Longitude</label>
								<Input
									name="longitude"
									value={form.longitude}
									onChange={handleChange}
									onBlur={e => {
										const val = e.target.value;
										let error = '';
										if (val === '' || isNaN(Number(val))) {
											error = 'Longitude must be a number';
										} else {
											const num = parseFloat(val);
											if (num < -180 || num > 180) error = 'Longitude must be between -180 and 180';
											if (/\.\d{9,}/.test(val)) error = 'Longitude max 8 decimals';
										}
										if (error) setInputErrors(prev => ({ ...prev, longitude: error }));
									}}
									inputMode="decimal"
									pattern="^-?(1[0-7]\d|0?\d{1,2})(\.\d{1,8})?$|^-?180(\.0{1,8})?$"
									disabled={isSubmitted}
									placeholder="e.g. -74.0060"
								/>
								{inputErrors.longitude && <div style={{ color: 'red' }}>{inputErrors.longitude}</div>}
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Site Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField>
								<label>ICC or Contractor?</label>
								<Dropdown
									name="icc_or_contractor"
									value={form.icc_or_contractor}
									options={[
										{ key: 'ICC', value: 'ICC', text: 'ICC' },
										{ key: 'CONTRACTOR', value: 'CONTRACTOR', text: 'Contractor' },
									]}
									onChange={handleChange}
									disabled={isSubmitted}
									selection
									placeholder="Select..."
								/>
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField>
								<label>Site Marked With</label>
								<Dropdown
									name="site_marked_with"
									value={form.site_marked_with}
									options={[
										{ key: 'PAINT', value: 'PAINT', text: 'Paint' },
										{ key: 'FLAG', value: 'FLAG', text: 'Flag' },
										{ key: 'OTHER', value: 'OTHER', text: 'Other' },
									]}
									onChange={handleChange}
									disabled={isSubmitted}
									selection
									placeholder="Select..."
								/>
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField>
								<label>Were the marks accurate?</label>
								<Form.Group inline>
									<Form.Radio
										label="Yes"
										name="marks_accurate"
										value={true}
										checked={form.marks_accurate === true}
										onChange={(e, { name, value }) => handleChange(e, { name, value })}
										disabled={isSubmitted}
									/>
									<Form.Radio
										label="No"
										name="marks_accurate"
										value={false}
										checked={form.marks_accurate === false}
										onChange={(e, { name, value }) => handleChange(e, { name, value })}
										disabled={isSubmitted}
									/>
								</Form.Group>
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Incident Information</Header>
					<Grid>
						<GridColumn width={4}>
							<FormField required error={!!inputErrors.damage_date_time}>
								<label>Date of Damage</label>
								<IcctDatePicker
									keyName="damage_date"
									value={(() => {
										if (form.damage_date instanceof Date && !isNaN(form.damage_date)) return form.damage_date;
										if (typeof form.damage_date === 'string' && form.damage_date) {
											const [month, day, year] = form.damage_date.split('/');
											if (month && day && year) {
												return new Date(Number(year), Number(month) - 1, Number(day));
											}
										}
										return null;
									})()}
									onChange={(key, date) => {
										setForm(prev => ({
											...prev,
											[key]: date ? `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}` : ''
										}));
										if (inputErrors.damage_date_time) {
											setInputErrors(prev => ({ ...prev, damage_date_time: null }));
										}
									}}
									disabled={isSubmitted}
									placeholder="MM/DD/YYYY"
								/>
								{inputErrors.damage_date_time && <div style={{ color: 'red' }}>{inputErrors.damage_date_time}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={4}>
							<FormField required error={!!inputErrors.damage_date_time}>
								<label>Time of Damage</label>
								<Input
									type="time"
									name="damage_time"
									value={form.damage_time}
									onChange={(e, data) => handleTimeChange(e, data)}
									disabled={isSubmitted}
									placeholder="HH:mm"
								/>
								{inputErrors.damage_date_time && <div style={{ color: 'red' }}>{inputErrors.damage_date_time}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.utility_type}>
								<label>Utility Type</label>
								<Input name="utility_type" value={form.utility_type} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.utility_type && <div style={{ color: 'red' }}>{inputErrors.utility_type}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.tds_project_number}>
								<label>TDS Project Number/TC-xxxxxx</label>
								<Input name="tds_project_number" value={form.tds_project_number} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.tds_project_number && <div style={{ color: 'red' }}>{inputErrors.tds_project_number}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.utility_damaged}>
								<label>Utility Damaged</label>
								<Input name="utility_damaged" value={form.utility_damaged} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.utility_damaged && <div style={{ color: 'red' }}>{inputErrors.utility_damaged}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={16}>
							<FormField>
								<label>If other, describe utility damaged</label>
								<TextArea name="utility_damaged_other_description" value={form.utility_damaged_other_description} onChange={handleChange} disabled={isSubmitted} rows={2} />
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">811 Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField>
								<label>Locate state 811 contacted?</label>
								<Form.Checkbox
									name="locate_state_811_contacted"
									checked={form.locate_state_811_contacted}
									onChange={handleChange}
									disabled={isSubmitted}
									label="Yes"
								/>
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.dig_811_ticket_number}>
								<label>811/Miss Dig Ticket Number</label>
								<Input name="dig_811_ticket_number" value={form.dig_811_ticket_number} onChange={handleChange} disabled={isSubmitted} />
								{inputErrors.dig_811_ticket_number && <div style={{ color: 'red' }}>{inputErrors.dig_811_ticket_number}</div>}
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Excavation Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField>
								<label>Type of Excavation Used</label>
								<Input name="excavation_type_used" value={form.excavation_type_used} onChange={handleChange} disabled={isSubmitted} />
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField required error={!!inputErrors.damage_depth_inches}>
								<label>Depth of Damage in Inches</label>
								<Input
									name="damage_depth_inches"
									value={form.damage_depth_inches}
									onChange={handleChange}
									onBlur={e => {
										const error = validateDepth(e.target.value);
										if (error) setInputErrors(prev => ({ ...prev, damage_depth_inches: error }));
									}}
									inputMode="decimal"
									pattern="^\d+(\.\d{1,2})?$"
									disabled={isSubmitted}
									placeholder="e.g. 12.5"
								/>
								{inputErrors.damage_depth_inches && <div style={{ color: 'red' }}>{inputErrors.damage_depth_inches}</div>}
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Incident Description</Header>
					<FormField>
						<label>Describe what happened</label>
						<TextArea name="describe_what_happened" value={form.describe_what_happened} onChange={handleChange} disabled={isSubmitted} rows={3} />
					</FormField>
				</Segment>
				<Segment>
					<Header as="h4">Evacuation Information</Header>
					<Grid>
						<GridColumn width={8}>
							<FormField>
								<label>Evacuation Required?</label>
								<Form.Checkbox
									name="evacuation_required"
									checked={form.evacuation_required}
									onChange={handleChange}
									disabled={isSubmitted}
									label="Yes"
								/>
							</FormField>
						</GridColumn>
						<GridColumn width={8}>
							<FormField error={!!inputErrors.evacuation_how_many}>
								<label>How many people were evacuated?</label>
								<Input
									name="evacuation_how_many"
									value={form.evacuation_how_many}
									onChange={handleChange}
									disabled={isSubmitted}
									placeholder="Enter number of people"
									type="number"
									min="0"
								/>
								{inputErrors.evacuation_how_many && <div style={{ color: 'red' }}>{inputErrors.evacuation_how_many}</div>}
							</FormField>
						</GridColumn>
						<GridColumn width={16}>
							<FormField>
								<label>Evacuation Notes</label>
								<TextArea name="evacuation_notes" value={form.evacuation_notes} onChange={handleChange} disabled={isSubmitted} rows={2} />
							</FormField>
						</GridColumn>
					</Grid>
				</Segment>
				<Segment>
					<Header as="h4">Additional Information</Header>
					<FormField>
						<label>Additional Notes</label>
						<TextArea name="additional_notes" value={form.additional_notes} onChange={handleChange} disabled={isSubmitted} rows={2} />
					</FormField>
					<FormField>
						<label>Attachments (Image Only)</label>
						<Input
							type="file"
							name="attachments"
							multiple
							accept="image/*"
							disabled={isSubmitted}
							onChange={async e => {
								const files = e.target.files;
								if (!files || files.length === 0) {
									return setForm(f => ({ ...f, attachments: [] }));
								} const base64Images = await Promise.all(Array.from(files).map(file => {
									return new Promise((resolve, reject) => {
										const reader = new FileReader();
										reader.onload = () => resolve(reader.result);
										reader.onerror = reject;
										reader.readAsDataURL(file);
									});
								}));
								setForm(f => ({ ...f, attachments: base64Images }));
							}}
						/>
						{form.attachments && form.attachments.length > 0 && (
							<div style={{ marginTop: 8, display: 'flex', gap: 12 }}>
								{form.attachments.map((img, idx) => (
									<div key={idx}>
										<img
											src={img}
											alt={`attachment-${idx}`}
											style={{ maxWidth: 100, maxHeight: 100, borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer' }}
											onClick={() => {
												setModalImageSrc(img);
												setShowImageModal(true);
											}}
										/>
									</div>
								))}
							</div>
						)}
					</FormField>
				</Segment>
				<Segment>
					<Header as="h4">Team</Header>
					<FormField required error={!!inputErrors.team}>
						<label>Team</label>
						<Dropdown
							placeholder="Select team"
							fluid
							search
							selection
							name="team"
							value={form.team}
							options={teams}
							onChange={handleChange}
							disabled={isSubmitted}
						/>
						{inputErrors.team && <div style={{ color: 'red' }}>{inputErrors.team}</div>}
					</FormField>
				</Segment>
				{!isSubmitted && !isReadOnly && (
				<div style={{ marginTop: '20px' }}>
					<Button
						type="button"
						onClick={saveDraft}
						loading={saving}
						disabled={saving}
						style={{ marginRight: '10px' }}
						color="green"
					>
						Save as Draft
					</Button>
					<Button
						type="button"
						onClick={() => navigate('/forms/utilitystrike')}
						disabled={saving}
						style={{ marginLeft: '10px' }}
					>
						Cancel
					</Button>
					<Button
						type="button"
						color="blue"
						onClick={handleSubmitClick}
						loading={saving}
						disabled={saving}
						style={{ marginLeft: '10px' }}
					>
						Submit
					</Button>
				</div>
			)}
				{isSubmitted && (
					<div style={{ marginTop: '20px' }}>
						<Button
							type="button"
							onClick={() => navigate('/forms/utilitystrike')}
						>
							Back to Utility Strike List
						</Button>
					</div>
				)}
				<div style={{ height: '40px' }}></div>
			</Form>
			<Confirm
				open={showConfirm}
				header="Submit Utility Strike Form"
				content="Submitting this form will finalize it and it cannot be retrieved for additional edits. Are you sure you want to proceed?"
				confirmButton="Yes, Submit"
				cancelButton="Cancel"
				onConfirm={handleConfirmSubmit}
				onCancel={handleCancelSubmit}
			/>
			<Modal
				open={showImageModal}
				onClose={() => setShowImageModal(false)}
				size="large"
				closeIcon
				style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
			>
				<Modal.Content style={{ textAlign: 'center' }}>
					{modalImageSrc && (
						<img src={modalImageSrc} alt="Full Attachment" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8, border: '2px solid #ccc' }} />
					)}
				</Modal.Content>
			</Modal>
		</div>
	);
};
